import React, {useState, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Modal} from '@/components/atoms';
import {UploadFile} from '@/components/molecules';
import {Constants} from '@/utils/constants/commonConstants';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';

const UploadMediaModal = ({
  uploadFor = null,
  openUpload,
  setOpenUpload,
  formData,
  setFormData,
  title = 'Upload Media',
  showTertiaryButton = false,
  ...props
}) => {
  const [uploadError, setUploadError] = useState(false);
  const [secondaryBtnText, setSecondaryBtnText] = useState('Cancel');
  const [uploading, setUploading] = useState(false);

  const handleDelete = useCallback(() => {
    setFormData((prev) => ({...prev, ...{targetMediaFile: []}}));
  }, [setFormData]);

  const isCloseModal = useCallback(() => {
    setOpenUpload(false);
    handleDelete();
    setSecondaryBtnText(Constants.Cancel);
  }, [setOpenUpload, handleDelete]);

  const isFileError = useMemo(
    () => secondaryBtnText !== Constants.Cancel,
    [secondaryBtnText]
  );

  return (
    <Modal
      isDark={true}
      onCloseModal={() => {
        handleDelete();
        isCloseModal();
        setUploadError(false);
      }}
      onClickSecondaryButton={() => {
        handleDelete();
        isCloseModal();
        setUploadError(false);
      }}
      onClickPrimaryButton={isCloseModal}
      showModal={openUpload}
      className='upload-target-modal'
      title={title}
      primaryButtonText={'Done'}
      primaryButtonDisabled={
        isFileError || formData?.targetMediaFile?.length <= 0
      }
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={true}
      secondaryButtonText={secondaryBtnText}
      data-testId='upload-target-modal'
      showTertiaryButton={isFileError && showTertiaryButton}
      onClickTertiaryButton={isCloseModal}
      tertiaryButtonText='Cancel'
      content={
        <UploadFile
          uploadFor={uploadFor}
          acceptedExtensions='.jpg,.png'
          description='Drag and drop your file here (only .jpg,.png)'
          label='Upload media files'
          variant={Constants.Edit}
          feedbackType={uploadError ? Constants.Error : Constants.Info}
          required={true}
          files={
            formData?.targetMediaFile?.length > 0
              ? [formData?.targetMediaFile]
              : []
          }
          onUpload={(file) => {
            setFormData((prev) => ({
              ...prev,
              ...{targetMediaFile: [file?.name]}
            }));
            setUploadError(false);
          }}
          onUploadDelete={handleDelete}
          helperText={
            uploadError
              ? 'File format is not valid. Supported formats: .csv'
              : ''
          }
          onUploadError={(error) => {
            if (error) {
              setUploadError(true);
            }
          }}
          type={Constants.Single}
          showDownloadTemplate={false}
          showHandleViewTable={false}
          className={
            formData?.targetMediaFile?.length > 0 && isFileError
              ? 'disabled'
              : ''
          }
          containerClassName={
            formData?.targetMediaFile?.length > 0 ? 'disabled' : ''
          }
          dataTestId='upload-file'
          setSecondaryBtnText={setSecondaryBtnText}
          uploading={uploading}
          setUploading={setUploading}
          {...props}
        />
      }
    />
  );
};

UploadMediaModal.propTypes = {
  uploadFor: PropTypes.string,
  openUpload: PropTypes.bool.isRequired,
  setOpenUpload: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    targetMediaFile: PropTypes.array
  }),
  setFormData: PropTypes.func.isRequired,
  title: PropTypes.string,
  showTertiaryButton: PropTypes.bool
};

export default React.memo(UploadMediaModal);
